import React, { type MouseEvent } from 'react';
import isEmpty from 'lodash/isEmpty';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics';
import { fg } from '@atlassian/jira-feature-gating';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import {
	type Attributes,
	useAnalyticsEvents,
	fireUIAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { ADD } from '@atlassian/jira-software-add-comment-on-flag-action/src/ui/constants';
import { useFireCheckboxAnalytics } from '@atlassian/jira-software-context-menu/src/controllers/analytics/main/main.tsx';
import { useEvent } from '@atlassian/jira-software-react-use-event';
import { FlagWithCommentType } from '../../../../model/modals/types';
import type { CardForFlagWithCommentModal, CardId, Card } from '../../../../model/types';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../state';
import { cardGroupToggle } from '../../../../state/actions/card-list';
import { cardFocus, cardBlur } from '../../../../state/actions/cards/focus';
import { cardClick, cardSingleSelect } from '../../../../state/actions/cards/selection';
import { openModal } from '../../../../state/actions/modals';
import {
	getIsCompanyManaged,
	getProjectIds,
	getProjectAris,
	getStoryPointFieldId,
	getTimeTrackingOptions,
	projectIdSelector,
} from '../../../../state/selectors/app';
import {
	getCards,
	getSelectedCards,
	getFocusedCard,
	isCardSelected,
	hasCardExtraFields,
	makeGetCardGroupExpanded,
	getOrderedCardIdsByCardListIdWithLimit,
	cardsForFlagWithCommentModalFromCardIdsSelector,
	backlogHasFlaggedCards,
	backlogHasPriority,
	backlogHasDueDate,
	backlogHasStoryPoints,
	backlogHasEstimates,
	getOrderedVersions,
} from '../../../../state/selectors/cards';
import { isUserAtLeastMemberOfProject } from '../../../../state/selectors/config';
import { getEstimationType } from '../../../../state/selectors/estimation';
import { getFilters } from '../../../../state/selectors/filters';
import { issueParentsSelector } from '../../../../state/selectors/issue-parent';
import { canEditIssue } from '../../../../state/selectors/permissions';
import { getCardSelectionAnalyticsInputSource } from './utils';
import CardContents from './view';

export type Props = {
	isDragging: boolean;
	/**
	 * True when this element is a pragmatic DnD compact native drag handle.
	 */
	isDragHandle?: boolean;
	/**
	 * When true render slower interactive elements. It'll be true on hover and focus.
	 */
	shouldRenderRichCard: boolean;
	canShowStatus: boolean;
	id: CardId;
	isAttachedToParent?: boolean;
	subtaskIds: CardId[] | undefined;
	onSelectIssues?: () => void;
};

function CardContentsContainer({
	id,
	isDragging,
	isDragHandle,
	shouldRenderRichCard,
	isAttachedToParent = false,
	subtaskIds = undefined,
	onSelectIssues,
}: Props) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const cards = useBacklogSelector(getCards);
	// There is always the possibility that the card selected does not exist.
	const card: Card | undefined = cards[String(id)];
	const storyPointFieldId = useBacklogSelector(getStoryPointFieldId);
	const estimationType = useBacklogSelector(getEstimationType);
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);
	const isUserAtLeastMember = useBacklogSelector(isUserAtLeastMemberOfProject);
	const canUserEditIssue = useBacklogSelector(canEditIssue);
	const currentProjectId = useBacklogSelector(projectIdSelector);
	const projectIds = useBacklogSelector(getProjectIds);
	const projectAris = useBacklogSelector(getProjectAris);
	const parents = useBacklogSelector(issueParentsSelector);
	const selectedCards = useBacklogSelector(getSelectedCards);
	const isFocused = useBacklogSelector((state) => getFocusedCard(state) === id);
	const isSelected = useBacklogSelector((state) => isCardSelected(state)(id));
	const highlightText = useBacklogSelector((state) => getFilters(state).TEXT ?? null);
	const hasExtraFields = useBacklogSelector(hasCardExtraFields);
	const fireCheckboxAnalytics = useFireCheckboxAnalytics();

	const {
		key: cardKey,
		priority,
		summary,
		issueUrl,
		isDone,
		icon,
		numCompleteChildren,
		numTotalChildren,
		parentId,
		parentKey,
		dueDate,
		isFlagged,
		extraFields,
		typeHierarchyLevel: issueHierarchyLevel,
	} = card;

	const onClick = useBacklogActionCreator(
		(
			withCmd: boolean,
			withShift: boolean,
			source: 'mouse' | 'keyboard',
			// eslint-disable-next-line @typescript-eslint/no-inferrable-types
			viaCheckbox: boolean = false,
			externalAction?: ExternalAction,
		) => {
			getLongTasksMetrics('bento').start('backlog');
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'issue',
				}),
				'backlogCard',
				{
					source,
					issueHierarchyLevel,
				},
			);

			const fireSelectionAnalytics = (extraAttr: Attributes) => {
				if (viaCheckbox || withCmd || (withShift && extraAttr.numSelectedCards > 1)) {
					fireCheckboxAnalytics?.(
						createAnalyticsEvent({
							action: 'changed',
							actionSubject: 'checkbox',
						}),
						'backlogCard',
						{
							...extraAttr,
							inputSource: getCardSelectionAnalyticsInputSource(withShift, withCmd, viaCheckbox),
							isCheckboxChecked: !isSelected,
							issueHierarchyLevel,
							selectType: 'individual',
							issueId: id,
						},
					);
				}
			};
			return cardClick(id, withCmd, withShift, viaCheckbox, fireSelectionAnalytics, externalAction);
		},
	);

	const singleSelectCard = useBacklogActionCreator(() => cardSingleSelect(id));

	const onContextMenu = useEvent(() => {
		const isCardOutsideOfSelectedCards = !selectedCards.includes(id);
		if (isCardOutsideOfSelectedCards) {
			singleSelectCard();
		}
	});

	const onFocus = useBacklogActionCreator(() => cardFocus(id));
	const onBlur = useBacklogActionCreator(cardBlur);

	const onToggleCardGroup = useBacklogActionCreator(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'changed',
				actionSubject: 'expansionState',
			}),
			'backlogCardGroup',
			{ issueHierarchyLevel },
		);
		return cardGroupToggle(id);
	});

	const isGroupExpanded = useBacklogSelector((state) =>
		__SERVER__
			? makeGetCardGroupExpanded(state)(id) ||
				!!getOrderedCardIdsByCardListIdWithLimit(state)[id].length
			: makeGetCardGroupExpanded(state)(id),
	);

	const selectedCardsForFlagWithCommentModal: CardForFlagWithCommentModal[] = useBacklogSelector(
		(state) => cardsForFlagWithCommentModalFromCardIdsSelector(state)([id]),
	);

	const onAddFlag = useBacklogActionCreator((_: MouseEvent) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'inlineCardField',
		});
		fireUIAnalytics(analyticsEvent, 'addFlag');
		return openModal({
			type: FlagWithCommentType,
			payload: {
				flagAction: ADD,
				cards: selectedCardsForFlagWithCommentModal,
			},
		});
	});

	const hasFlaggedCards = useBacklogSelector(backlogHasFlaggedCards);
	const hasCardsWithPriority = useBacklogSelector(backlogHasPriority);
	const hasCardsWithDueDate = useBacklogSelector(backlogHasDueDate);
	const hasCardsWithStoryPoint = useBacklogSelector(backlogHasStoryPoints);
	const hasCardsWithEstimate = useBacklogSelector(backlogHasEstimates);
	const orderedVersions = useBacklogSelector(getOrderedVersions);

	if (!card) {
		return null;
	}

	/*
	 * as long as there is a single card having fix version, the orderedVersions is not empty
	 * checking the orderedVersions value against empty to determine if we should preserve the space for version
	 * even this card has no version
	 * the reason to do that is to to make sure the subtask text and subtask icons are aligned.
	 * https://hello.jira.atlassian.cloud/browse/FTFNS-54
	 * https://hello.jira.atlassian.cloud/browse/FTFNS-55
	 */
	const shouldDisplayVersions = isCompanyManaged && !isEmpty(orderedVersions);
	const isSubtask = isSubtaskHierarchyLevel(issueHierarchyLevel);

	return (
		<ContextualAnalyticsData attributes={{ issueHierarchyLevel }}>
			<CardContents
				cardId={id}
				isSelected={isSelected}
				selectedCards={selectedCards}
				numSelectedCards={selectedCards.length}
				parents={parents}
				currentProjectId={currentProjectId}
				projectIds={projectIds}
				projectAris={projectAris}
				storyPointFieldId={storyPointFieldId}
				isUserAtLeastMember={isUserAtLeastMember}
				canUserEditIssue={canUserEditIssue}
				isDragging={isDragging}
				isDragHandle={isDragHandle}
				estimationType={estimationType}
				timeTrackingOptions={timeTrackingOptions}
				shouldRenderRichCard={shouldRenderRichCard}
				highlightText={highlightText}
				isFocused={isFocused}
				hasExtraFields={hasExtraFields}
				onClick={onClick}
				onFocus={onFocus}
				onBlur={onBlur}
				onContextMenu={onContextMenu}
				isAttachedToParent={isAttachedToParent}
				isSubtask={isSubtask}
				subtaskIds={subtaskIds}
				isCardGroupExpanded={isGroupExpanded}
				onToggleCardGroup={onToggleCardGroup}
				cardKey={cardKey}
				dueDate={dueDate}
				icon={icon}
				isDone={isDone}
				isFlagged={isFlagged}
				issueUrl={issueUrl}
				numCompleteChildren={numCompleteChildren}
				numTotalChildren={numTotalChildren}
				parentId={parentId}
				parentKey={parentKey}
				priority={priority}
				shouldDisplayVersions={shouldDisplayVersions}
				summary={summary}
				extraFields={extraFields}
				onAddFlag={onAddFlag}
				shouldRenderInlineAddFlag={hasFlaggedCards}
				shouldRenderPriorityColumn={hasCardsWithPriority}
				shouldRenderDueDateColumn={hasCardsWithDueDate}
				shouldRenderStoryPointColumn={hasCardsWithStoryPoint}
				shouldRenderEstimatesColumn={hasCardsWithEstimate}
				{...(fg('bulk_operations_toolbar_milestone_1')
					? {
							onSelectIssues,
						}
					: {})}
			/>
		</ContextualAnalyticsData>
	);
}

export default CardContentsContainer;
